import { useEffect, useMemo, useRef, useState } from "react";
import { Wrench, X } from "lucide-react";
import type { DailyEntryOptions } from "@crewledger/shared/schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { groupEquipment } from "./utils";

export type ApplyEquipmentTarget = "crew" | "selected";

export interface ApplyEquipmentSelection {
  equipmentIds: string[];
  hoursMode: "match_labor" | "custom";
  customHours: number;
  mergeMode: "replace" | "append";
}

interface ApplyEquipmentModalProps {
  open: boolean;
  onClose: () => void;
  options: DailyEntryOptions;
  target: ApplyEquipmentTarget;
  targetLabel: string;
  workerCount: number;
  canViewEquipmentCosts: boolean;
  onApply: (selection: ApplyEquipmentSelection) => void;
}

export function ApplyEquipmentModal({
  open,
  onClose,
  options,
  target,
  targetLabel,
  workerCount,
  canViewEquipmentCosts,
  onApply,
}: ApplyEquipmentModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [hoursMode, setHoursMode] = useState<"match_labor" | "custom">("match_labor");
  const [customHours, setCustomHours] = useState(8);
  const [mergeMode, setMergeMode] = useState<"replace" | "append">("replace");

  const equipmentGroups = useMemo(() => groupEquipment(options.equipment), [options.equipment]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setHoursMode("match_labor");
      setCustomHours(8);
      setMergeMode("replace");
    }
  }, [open]);

  const toggleEquipment = (equipmentId: string) => {
    setSelectedIds((prev) =>
      prev.includes(equipmentId)
        ? prev.filter((id) => id !== equipmentId)
        : [...prev, equipmentId],
    );
  };

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 m-auto w-full max-w-lg rounded-xl border-0 bg-card-white p-0 shadow-[var(--shadow-elevated)] backdrop:bg-navy-deep/50"
      onClose={onClose}
    >
      <div className="p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-navy-deep">
              <Wrench className="h-5 w-5" />
              Apply equipment
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {target === "crew"
                ? `Apply to entire crew: ${targetLabel}`
                : `Apply to ${workerCount} selected worker${workerCount === 1 ? "" : "s"}`}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 hover:bg-muted" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Equipment</Label>
            <div className="max-h-48 space-y-3 overflow-y-auto rounded-lg border p-3">
              {equipmentGroups.map((group) => (
                <div key={group.key}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {group.label}
                  </p>
                  <div className="mt-1 space-y-1">
                    {group.items.map((item) => (
                      <label
                        key={item.id}
                        className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-bg-light"
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => { toggleEquipment(item.id); }}
                          className="rounded border-input"
                        />
                        <span className="text-sm">
                          {item.equipmentId} — {item.name}
                          {item.status !== "AVAILABLE" ? (
                            <span className="ml-1 text-xs text-amber-700">({item.status})</span>
                          ) : null}
                          {canViewEquipmentCosts && item.hourlyCost != null
                            ? ` · $${item.hourlyCost}/hr`
                            : ""}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Hours</Label>
              <select
                value={hoursMode}
                onChange={(e) => { setHoursMode(e.target.value as "match_labor" | "custom"); }}
                className="flex h-10 w-full rounded-md border border-input px-3 text-sm"
              >
                <option value="match_labor">Match worker hours</option>
                <option value="custom">Custom hours</option>
              </select>
            </div>
            {hoursMode === "custom" ? (
              <div className="space-y-2">
                <Label>Custom hours</Label>
                <Input
                  type="number"
                  min={0}
                  max={24}
                  step={0.5}
                  value={customHours}
                  onChange={(e) => { setCustomHours(Number(e.target.value) || 0); }}
                  className="h-10"
                />
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label>Apply mode</Label>
            <select
              value={mergeMode}
              onChange={(e) => { setMergeMode(e.target.value as "replace" | "append"); }}
              className="flex h-10 w-full rounded-md border border-input px-3 text-sm"
            >
              <option value="replace">Replace existing equipment</option>
              <option value="append">Add to existing equipment</option>
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onApply({
                equipmentIds: selectedIds,
                hoursMode,
                customHours,
                mergeMode,
              });
              onClose();
            }}
            disabled={selectedIds.length === 0 || workerCount === 0}
          >
            Apply equipment
          </Button>
        </div>
      </div>
    </dialog>
  );
}
